import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import StrokeWeightMediumIcon from "@atlaskit/icon/core/stroke-weight-medium";

import { useAdg4TabsContext } from "@/components/adg4/tabs/src";
import { useUserContext } from "@/components/user-context/user-context-provider";
import { Link } from "@/libraries/design/components";
import { useLocalizedHref } from "@/libraries/web-framework";

import { tabbedHeroSectionSpacesMessages } from "../messages/tabbed-hero-section.messages";

import { ContentTypeVideo } from "./content-type-video";

import type { TabProps, TabType } from "./tab-type";

type SpacesContentTypeProps = TabProps & {
    isConfluenceCrossflow?: boolean;
};

export const SpacesContentType: TabType = ({
    index,
    isConfluenceCrossflow
}: SpacesContentTypeProps) => {
    const { isCurrentTab } = useAdg4TabsContext();
    const intl = useIntl();
    const ctaLink = useLocalizedHref(
        "/software/confluence/use-cases/knowledge-management-software"
    );
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { user } = useUserContext();

    return (
        <ContentTypeVideo
            active={isCurrentTab(index)}
            title={intl.formatMessage(
                tabbedHeroSectionSpacesMessages.contentTitle
            )}
            description={intl.formatMessage(
                tabbedHeroSectionSpacesMessages.contentDescription
            )}
            iconTitle={intl.formatMessage(
                tabbedHeroSectionSpacesMessages.contentIconTitle
            )}
            iconCollection={[
                {
                    description: intl.formatMessage(
                        tabbedHeroSectionSpacesMessages.contentFeaturePoint1
                    ),
                    icon: <StrokeWeightMediumIcon label="" />
                },
                {
                    description: intl.formatMessage(
                        tabbedHeroSectionSpacesMessages.contentFeaturePoint2
                    ),
                    icon: <StrokeWeightMediumIcon label="" />
                },
                {
                    description: intl.formatMessage(
                        tabbedHeroSectionSpacesMessages.contentFeaturePoint3
                    ),
                    icon: <StrokeWeightMediumIcon label="" />
                }
            ]}
            cta={
                /* eslint-disable-next-line @atlassian/a11y/anchor-has-content, @atlassian/a11y/link-name */
                <Link
                    href={ctaLink}
                    text={intl.formatMessage(
                        tabbedHeroSectionSpacesMessages.contentCta
                    )}
                    type={"primary"}
                    dataEventProps={
                        !isConfluenceCrossflow
                            ? {
                                  "data-product-key": "confluence",
                                  "data-event-component": "linkButton",
                                  "data-event-container": "contentTypesSection",
                                  "data-event": "clicked",
                                  "data-label": "explore-knowledge-sharing",
                                  "data-label-english":
                                      "Explore knowledge sharing",
                                  "data-track-event":
                                      "confluence-pt.content-types.spaces.cta"
                              }
                            : undefined
                    }
                    onClick={() => {
                        if (!isConfluenceCrossflow) {
                            return;
                        }
                        createAnalyticsEvent({
                            eventName: "confluence-crossflow-tab-cta-clicked",
                            eventType: "ui",
                            action: "clicked",
                            actionSubject: "linkButton",
                            actionSubjectId: "confluenceCrossFlowHeroSection",
                            attributes: {
                                reactComponent: "SpacesContentType",
                                tab: "spaces",
                                userTeamType: user.extended_profile?.team_type
                            }
                        }).fire();
                    }}
                />
            }
            reverseLayout
            videoSrcMp4="https://wac-cdn.atlassian.com/misc-assets/videos/confluence-product-tour/PT_CT_Spaces_LowBR_reencode.mp4"
            videoSrcWebm="https://wac-cdn.atlassian.com/misc-assets/videos/confluence-product-tour/PT_CT_Spaces_LowBR.webm"
            videoPreviewSrc="https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/PT_CT_Spaces_Preview.webp"
        />
    );
};
